/**
 * PATH: packages/bills/src/components/BillsQueueHeader.tsx
 * FILE_ID: BILLS-PKG-CMP-QUEUE-HEADER-001
 * PURPOSE: The queue header — the money line (known / unknown /
 *          overdue, server-computed over the FULL filtered set) and the
 *          filter rail (All / Extracted / Manual review / Couldn't
 *          fetch).
 *
 * WHY: Operators work three queues; everything else was chip noise. The
 *      rail is a deliberate subset of the pipeline, and the list is
 *      always newest-first — no sort toggle to reason about. Money
 *      figures are only ever server-computed over the whole filtered
 *      set (never a sum of the loaded page).
 *
 * DEPENDENCIES: react, lucide-react, @quotech/ui, ./billsQueueModel.
 */

import { AlertCircle, Clock3 } from 'lucide-react';
import { FilterChip } from '@quotech/ui';
import type { BillsListResponse } from '../types';
import {
  buildMoneyLine,
  buildPipelinePartition,
  type QueueFilter,
} from './billsQueueModel';
import { formatCurrencyWhole } from './billsListLeaves';

interface Props {
  data: BillsListResponse | null;
  filter: QueueFilter;
  onChangeFilter: (next: QueueFilter) => void;
}

export function BillsQueueHeader({ data, filter, onChangeFilter }: Props) {
  const partition = buildPipelinePartition(data?.counts);
  const money = buildMoneyLine(data?.summary);
  const primary = money?.currencies[0];
  const extraCurrencies = money ? money.currencies.slice(1) : [];

  const isAll = filter.status === 'all' && !filter.flag;

  return (
    <div className="space-y-2">
      {/* ── The money line. Known sum only; unknowns are their own count,
          never folded into the total behind a "+". Server-computed over
          the FULL current filter, so it changes with the chips. */}
      {money && primary && (
        <div className="space-y-1">
          <div className="flex items-baseline justify-between gap-2">
            <span
              className="text-lg font-semibold tabular-nums tracking-tight text-foreground"
              title={`Sum of the ${primary.knownCount} bills in this view with a trustworthy amount (${primary.currency}). Bills with no amount are counted separately below.`}
            >
              {formatCurrencyWhole(primary.knownTotal, primary.currency)}
            </span>
            <span className="text-[11px] tabular-nums text-muted-foreground">
              {/* Scoped to the currency of the figure it sits beside —
                  other currencies are itemised on the "Also:" line so the
                  filter count always reconciles on screen. */}
              {primary.knownCount} with amounts ({primary.currency})
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px]">
            {money.unknownCount > 0 && (
              <span
                className="flex items-center gap-1 text-amber-700 dark:text-amber-400"
                title="Bills with no trustworthy amount — not included in the total above."
              >
                <AlertCircle className="h-3 w-3" aria-hidden />
                <span className="font-semibold tabular-nums">
                  {money.unknownCount.toLocaleString()}
                </span>
                <span>with no amount</span>
              </span>
            )}
            {primary.overdueCount > 0 && (
              <span
                className="flex items-center gap-1 text-rose-600 dark:text-rose-400"
                title="Past due date and not marked paid, within this view. Amount covers only the overdue bills with a known total."
              >
                <Clock3 className="h-3 w-3" aria-hidden />
                <span className="font-semibold tabular-nums">
                  {formatCurrencyWhole(primary.overdueTotal, primary.currency)}
                </span>
                <span>overdue · {money.overdueCount.toLocaleString()}</span>
              </span>
            )}
          </div>
          {extraCurrencies.length > 0 && (
            <div className="text-[10px] tabular-nums text-muted-foreground">
              {/* Never blend currencies into one number. */}
              Also:{' '}
              {extraCurrencies
                .map(
                  (c) =>
                    `${formatCurrencyWhole(c.knownTotal, c.currency)} ${c.currency} (${c.knownCount})`,
                )
                .join(' · ')}
            </div>
          )}
        </div>
      )}

      {/* ── The filter rail: All + the three working queues. The list
          itself is always newest-first. */}
      <div role="group" aria-label="Filter bills" className="flex flex-wrap items-center gap-1.5">
        <FilterChip
          active={isAll}
          count={partition.total}
          onClick={() => onChangeFilter({ status: 'all' })}
          title="Every bill, at every stage."
        >
          All
        </FilterChip>
        {partition.chips.map((c) => (
          <FilterChip
            key={c.key}
            active={filter.status === c.key && !filter.flag}
            count={c.count}
            onClick={() => onChangeFilter({ status: c.key })}
            title={c.title}
          >
            {c.label}
          </FilterChip>
        ))}
      </div>
    </div>
  );
}
